import { Spinner } from "blocks/components/Spinner";
import { trpc } from "utils/trpc";
import { TableBackedKanban, MoveCard } from "./TableBackedKanban";
import { ZeroState } from "./zero-states";
import { useMemo, useState } from "react";
import { ModalEditTableRecord } from "./ModalEditTableRecord";
import { APIRecord } from "server/services/tables/types/records";
import { usePageContext } from "lib/context/page-context";
import { KanbanHeader } from "./KanbanHeader";
import ModalKanbanCardDetail from "./ModalKanbanCardDetail";
import type { KanbanBlock as KanbanBlockType } from "../../types";
import type { BlockProps } from "blocks/v2/__shared__/types";
import { useTrackingContext } from "observability";

type ModalState =
  | { newRecord: Partial<APIRecord> }
  | { editRecordId: APIRecord["id"] }
  | { deleteRecordId: APIRecord["id"] }
  | { detailsRecordId: APIRecord["id"] }
  | null;

export const ConnectedLiveKanban = (props: BlockProps<KanbanBlockType>) => {
  const { emitConsumerInteractionEvent } = useTrackingContext();
  const { pageId, projectId, accountId, isEditing, isThumbnail, isEmbedded } =
    usePageContext();

  const [modalState, setModalState] = useState<ModalState>(null);

  const blockStateId = props.block.stateId;

  const { data: kanbanResponse, isLoading } = trpc.blockKanban.get.useQuery(
    { id: blockStateId as string },
    { enabled: !!props.block.stateId }
  );

  const utils = trpc.useContext();

  const { mutate } = trpc.blockKanban.moveCard.useMutation({
    onSuccess() {
      emitConsumerInteractionEvent({
        interaction_goal: "Drag kanban card",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "kanban",
        embedded_flag: isEmbedded,
      });
      if (props.block.stateId)
        utils.blockKanban.get.invalidate({ id: props.block.stateId });
      utils.tableRecords.query.refetch();
      utils.tableRecords.get.invalidate();
    },
  });

  const handleMoveCard: MoveCard | undefined = useMemo(() => {
    if (props.block.config.canMoveCards)
      return ({ movingCardId, targetCardId, placeAfter, toValue }) => {
        if (!props.block.stateId) return;

        mutate({
          id: props.block.stateId,
          movingCardId,
          targetCardId,
          placeAfter,
          toValue,
        });
      };
  }, [mutate, props.block.config.canMoveCards, props.block.stateId]);

  const handleClickCreate = useMemo(() => {
    if (props.block.config.canCreateCards)
      return (columnValue?: string) => {
        const recordData: APIRecord["data"] = {};

        if (columnValue) {
          const fieldKey = `f${props.block.config.groupFieldId}`;
          recordData[fieldKey] = { value: columnValue, label: columnValue };
        }

        setModalState({
          newRecord: {
            data: recordData,
          },
        });
      };
  }, [props.block.config.canCreateCards, props.block.config.groupFieldId]);

  const handleClickCard = useMemo(() => {
    if (
      props.block.config.canUpdateCards ||
      props.block.config.canDeleteCards
    ) {
      return (recordId: APIRecord["id"]) => {
        setModalState({ editRecordId: recordId });
      };
    } else {
      return (recordId: APIRecord["id"]) => {
        setModalState({ detailsRecordId: recordId });
      };
    }
  }, [props.block.config.canDeleteCards, props.block.config.canUpdateCards]);

  if (isLoading) return <Spinner height={200} />;

  if (!kanbanResponse || "message" in kanbanResponse)
    return <ZeroState title="Configure your component" />;

  return (
    <>
      <KanbanHeader
        title={props.block.config.title}
        newCardButtonCopy={props.block.config.newCardButtonCopy}
        onClickCreate={handleClickCreate}
      />
      <TableBackedKanban
        data={kanbanResponse}
        accountId={accountId}
        tableId={props.block.config.tableId}
        onMoveCard={handleMoveCard}
        onClickCreate={handleClickCreate}
        onClickCard={handleClickCard}
        disableScroll={isEditing || isThumbnail}
        isDisabled={isThumbnail}
      />
      {modalState &&
        ("editRecordId" in modalState ||
          "newRecord" in modalState ||
          "deleteRecordId" in modalState) &&
        accountId &&
        props.block.config.tableId && (
          <ModalEditTableRecord
            kanbanId={props.block.config.id}
            tableId={props.block.config.tableId}
            recordId={
              "editRecordId" in modalState ? modalState.editRecordId : undefined
            }
            newRecord={
              "newRecord" in modalState ? modalState.newRecord : undefined
            }
            onClosed={() => setModalState(null)}
            canDelete={props.block.config.canDeleteCards}
          />
        )}
      {modalState && accountId && "detailsRecordId" in modalState && (
        <ModalKanbanCardDetail
          accountId={accountId}
          kanbanId={props.block.config.id}
          recordId={modalState.detailsRecordId}
          onClosed={() => setModalState(null)}
        />
      )}
    </>
  );
};
